/**
 * TokenManager - Gerenciador singleton de tokens de autenticação
 *
 * Resolve race conditions garantindo que apenas um refresh de token
 * aconteça por vez, mesmo com múltiplas operações de sync simultâneas.
 */

import { syncLogger } from './SyncLogger'

class TokenManager {
  constructor() {
    this.refreshPromise = null
    this.currentToken = null
    this.tokenExpiry = null
    this.refreshAttempts = 0
    this.maxRefreshAttempts = 3
  }

  /**
   * Obtém um token válido, fazendo refresh se necessário
   * Múltiplas chamadas simultâneas aguardarão o mesmo refresh
   */
  async getValidToken(getLoggedUserFn, refreshTokenFn) {
    // Se já existe um refresh em andamento, aguardar
    if (this.refreshPromise) {
      syncLogger.log('TOKEN_WAIT_REFRESH', {
        reason: 'refresh_in_progress',
      })
      return this.refreshPromise
    }

    // Tentar obter token atual
    try {
      const userInfo = await getLoggedUserFn()

      if (userInfo?.token) {
        // Token disponível - verificar se ainda é válido
        if (!this._isTokenExpired(userInfo.token)) {
          this.currentToken = userInfo.token
          return userInfo.token
        }

        syncLogger.log('TOKEN_EXPIRED', {
          hasToken: true,
        })
      }
    } catch (error) {
      syncLogger.error('TOKEN_GET_ERROR', error)
    }

    // Tentar fallback do localStorage
    const fallbackToken = this._getFallbackToken()
    if (fallbackToken && !this._isTokenExpired(fallbackToken)) {
      syncLogger.log('TOKEN_FALLBACK_USED', {})
      this.currentToken = fallbackToken
      return fallbackToken
    }

    // Precisa fazer refresh
    return this._doRefresh(getLoggedUserFn, refreshTokenFn)
  }

  /**
   * Força um refresh do token
   */
  async forceRefresh(getLoggedUserFn, refreshTokenFn) {
    this.currentToken = null
    return this._doRefresh(getLoggedUserFn, refreshTokenFn)
  }

  /**
   * Executa o refresh do token com retry
   */
  async _doRefresh(getLoggedUserFn, refreshTokenFn) {
    // Criar promise única para todas as chamadas aguardarem
    this.refreshPromise = this._executeRefresh(getLoggedUserFn, refreshTokenFn)

    try {
      const token = await this.refreshPromise
      return token
    } finally {
      this.refreshPromise = null
    }
  }

  /**
   * Lógica interna de refresh com exponential backoff
   */
  async _executeRefresh(getLoggedUserFn, refreshTokenFn) {
    this.refreshAttempts = 0

    while (this.refreshAttempts < this.maxRefreshAttempts) {
      this.refreshAttempts++

      syncLogger.log('TOKEN_REFRESH_ATTEMPT', {
        attempt: this.refreshAttempts,
        maxAttempts: this.maxRefreshAttempts,
      })

      try {
        const result = await refreshTokenFn()

        if (result?.refreshed && result?.token) {
          // Refresh bem-sucedido
          this.currentToken = result.token
          this._saveFallbackToken(result.token)

          syncLogger.log('TOKEN_REFRESH_SUCCESS', {
            attempt: this.refreshAttempts,
          })

          return result.token
        }

        throw new Error('Refresh returned invalid result')
      } catch (error) {
        syncLogger.error('TOKEN_REFRESH_FAILED', error, {
          attempt: this.refreshAttempts,
        })

        if (this.refreshAttempts < this.maxRefreshAttempts) {
          // Aguardar antes de tentar novamente (exponential backoff)
          const delay = Math.pow(2, this.refreshAttempts) * 1000
          await this._sleep(delay)
        }
      }
    }

    // Todas as tentativas falharam
    syncLogger.error(
      'TOKEN_REFRESH_EXHAUSTED',
      new Error('Max attempts reached'),
      {
        attempts: this.refreshAttempts,
      },
    )

    // Última tentativa: usar fallback
    const fallback = this._getFallbackToken()
    if (fallback) {
      syncLogger.log('TOKEN_USING_STALE_FALLBACK', {})
      return fallback
    }

    return null
  }

  /**
   * Verifica se o token está expirado
   * Nota: Implementação simplificada - idealmente decodificaria o JWT
   */
  _isTokenExpired(token) {
    if (!token) return true

    // Tentar decodificar JWT para verificar expiração
    try {
      const parts = token.split('.')
      if (parts.length !== 3) return false // Não é JWT, assumir válido

      const payload = JSON.parse(atob(parts[1]))

      if (payload.exp) {
        // exp está em segundos, Date.now() em ms
        const expiryMs = payload.exp * 1000
        const now = Date.now()
        // Considerar expirado se faltar menos de 1 minuto
        return expiryMs - now < 60000
      }
    } catch {
      // Se não conseguir decodificar, assumir válido
    }

    return false
  }

  /**
   * Obtém token do fallback (localStorage)
   */
  _getFallbackToken() {
    try {
      return localStorage.getItem('fallback_token')
    } catch {
      return null
    }
  }

  /**
   * Salva token no fallback
   */
  _saveFallbackToken(token) {
    try {
      localStorage.setItem('fallback_token', token)
    } catch (e) {
      console.warn('[TokenManager] Failed to save fallback token:', e)
    }
  }

  /**
   * Limpa o token atual (para logout)
   */
  clearToken() {
    this.currentToken = null
    this.tokenExpiry = null
    try {
      localStorage.removeItem('fallback_token')
    } catch {
      // Ignorar erros de storage
    }
  }

  /**
   * Retorna o status atual do gerenciador
   */
  getStatus() {
    return {
      hasToken: !!this.currentToken,
      isRefreshing: !!this.refreshPromise,
      refreshAttempts: this.refreshAttempts,
      hasFallback: !!this._getFallbackToken(),
    }
  }

  /**
   * Helper para sleep
   */
  _sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms))
  }
}

// Singleton
export const tokenManager = new TokenManager()

export default TokenManager
